import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { IClassicDuelState } from '../../lib/interfaces/IClassicDuel';
import { IDuelState } from '../../lib/interfaces/IDuelSettings';
import { IQuickDuelState } from '../../lib/interfaces/IQuickDuel';
import { ITeamDuelState } from '../../lib/interfaces/ITeamDuel';
import { ITime } from '../../lib/interfaces/ITime';

interface ISettingsSlice {
  classicDuel: IClassicDuelState;
  teamDuel: ITeamDuelState;
  quickDuel: IQuickDuelState;
  duelSettings: IDuelState;
}

export const ClassicDuelInitialState: IClassicDuelState = {
  classicDuel: {
    roundTime: { min: 5, sec: 0 },
    isEnabledEndOfRoundSound: true,
  },
  pause: {
    isStartBlinking: false,
    isEnabledEndOfPauseSound: false,
    pauseTime: { min: 1, sec: 0 },
  },
  votersTimer: {
    isStartBlinking: false,
    isEnabledEndOfPauseSound: true,
    pauseTime: { min: 1, sec: 0 },
  },
};

export const QuickDuelInitialState: IQuickDuelState = {
  roundTime: { min: 1, sec: 0 },
  isEnabledEndOfRoundSound: true,
  votersTimer: {
    isStartBlinking: false,
    isEnabledEndOfPauseSound: true,
    pauseTime: { min: 1, sec: 0 },
  },
};

export const DuelInitialState: IDuelState = {
  player1: { min: 5, sec: 0 },
  player2: { min: 5, sec: 0 },
  isTimeSelected: false,
};

export const TeamDuelInitialState: ITeamDuelState = {
  teamDuel: {
    roundTime: { min: 7, sec: 0 },
    isEnabledEndOfRoundSound: true,
  },
  pause: {
    isStartBlinking: false,
    isEnabledEndOfPauseSound: false,
    pauseTime: { min: 1, sec: 0 },
  },
  votersTimer: {
    isStartBlinking: false,
    isEnabledEndOfPauseSound: true,
    pauseTime: { min: 1, sec: 0 },
  },
};

const initialState: ISettingsSlice = {
  classicDuel: ClassicDuelInitialState,
  teamDuel: TeamDuelInitialState,
  quickDuel: QuickDuelInitialState,
  duelSettings: DuelInitialState,
};

const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    setClassicDuel(state: ISettingsSlice, action: PayloadAction<IClassicDuelState>) {
      state.classicDuel = action.payload;
    },
    setTeamDuel(state: ISettingsSlice, action: PayloadAction<ITeamDuelState>) {
      state.teamDuel = action.payload;
    },
    resetClassicDuel(state: ISettingsSlice, action: PayloadAction) {
      state.classicDuel = ClassicDuelInitialState;
      state.duelSettings = DuelInitialState;
    },
    resetTeamDuel(state: ISettingsSlice, action: PayloadAction) {
      state.teamDuel = TeamDuelInitialState;
      state.duelSettings = DuelInitialState;
    },
    setQuickDuel(state: ISettingsSlice, action: PayloadAction<IQuickDuelState>) {
      state.quickDuel = action.payload;
    },
    resetQuickDuel(state: ISettingsSlice, action: PayloadAction) {
      state.quickDuel = QuickDuelInitialState;
      state.duelSettings = DuelInitialState;
    },
    setDuel(state: ISettingsSlice, action: PayloadAction<IDuelState>) {
      state.duelSettings = action.payload;
    },
    setPartialDuel(
      state: ISettingsSlice,
      action: PayloadAction<{ player: 'player1' | 'player2'; settings: ITime }>,
    ) {
      state.duelSettings[action.payload.player] = action.payload.settings;
    },
    resetDuel(state: ISettingsSlice, action: PayloadAction) {
      state.duelSettings = DuelInitialState;
    },
  },
});

export const settingsActions = settingsSlice.actions;

export default settingsSlice.reducer;
